import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, StatCard, Button, Badge } from '@/backoffice/components/ui';
import { fetchAdminDashboard } from '@/domains/admin/services/admin.service';
import { formatCurrency } from '@/backoffice/utils';
import { Users, Shield, BarChart3, TrendingUp, Globe, Building, Check, Loader2 } from 'lucide-react';

export function SuperAdminDashboard() {
  const [dashboard, setDashboard] = useState<Awaited<ReturnType<typeof fetchAdminDashboard>> | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAdminDashboard(true)
      .then(setDashboard)
      .catch(() => setDashboard(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !dashboard) {
    return (
      <DashboardLayout userType="super_admin" title="Administration générale">
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-violet-600" /></div>
      </DashboardLayout>
    );
  }

  const stats = dashboard.stats;

  return (
    <DashboardLayout userType="super_admin" title="Administration générale">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard title="Utilisateurs totaux" value={stats.total_users} icon={<Users className="w-6 h-6 text-blue-600" />} iconBg="bg-blue-50" />
        <StatCard title="Livreurs actifs" value={stats.active_drivers} icon={<Shield className="w-6 h-6 text-emerald-600" />} iconBg="bg-emerald-50" />
        <StatCard title="Commandes totales" value={stats.total_orders} icon={<BarChart3 className="w-6 h-6 text-purple-600" />} iconBg="bg-purple-50" />
        <StatCard title="Chiffre d'affaires" value={formatCurrency(stats.total_revenue)} icon={<TrendingUp className="w-6 h-6 text-amber-600" />} iconBg="bg-amber-50" />
      </div>

      <div className="grid md:grid-cols-6 gap-4 mb-6">
        {[
          { label: 'Admins actifs', value: stats.active_admins ?? 0, icon: <Shield /> },
          { label: 'Zones', value: stats.total_zones, icon: <Globe /> },
          { label: 'Villes', value: stats.cities_covered ?? stats.total_zones, icon: <Building /> },
          { label: 'Commandes/jour', value: stats.today_orders, icon: <BarChart3 /> },
          { label: 'Revenus/jour', value: formatCurrency(stats.today_revenue), icon: <TrendingUp /> },
          { label: 'Satisfaction', value: `${stats.average_satisfaction}/5`, icon: <TrendingUp /> },
        ].map((stat) => (
          <Card key={stat.label} className="text-center">
            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center mx-auto mb-2">{stat.icon}</div>
            <p className="text-xl font-bold">{stat.value}</p>
            <p className="text-xs text-gray-500">{stat.label}</p>
          </Card>
        ))}
      </div>

      <Card className="mb-6">
        <div className="flex justify-between mb-4">
          <h3 className="font-semibold">Administrateurs</h3>
          <Link to="/dashboard/super-admin/administrateurs"><Button size="sm">Voir tout</Button></Link>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          {(dashboard.staff_preview ?? []).map((admin) => (
            <div key={admin.id} className="p-4 bg-gray-50 rounded-lg">
              <p className="font-medium">{admin.name}</p>
              <p className="text-xs text-gray-500 truncate">{admin.email}</p>
              <Badge variant={admin.status === 'active' ? 'success' : 'default'} className="mt-2">{admin.status}</Badge>
            </div>
          ))}
        </div>
      </Card>
      
    </DashboardLayout>
  );
}
